import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { API_BASE_URL } from '../../utils/api'
import { BaseWidget } from './BaseWidget'
import { BriefcaseIcon } from '../Icons'
import { DesignTheme } from './FinancialWidgetDesignSelector'

interface LenderMetricsWidgetProps {
  className?: string
  designTheme?: DesignTheme
  widgetId?: string
}

interface LenderMetrics {
  totalLenders: number
  dominantLender: {
    id: number
    name: string
    percentage: number
  } | null
  totalDeals: number
  period: string
}

const LenderMetricsWidget: React.FC<LenderMetricsWidgetProps> = ({ className, widgetId }) => {
  const { t } = useTranslation()
  const [data, setData] = useState<LenderMetrics | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch(`${API_BASE_URL}/api/lenders/metrics`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const result = await response.json()
        setData(result.metrics)
      }
    } catch (error) {
      console.error('Error fetching lender metrics:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  return (
    <BaseWidget
      title={t('dashboard.widgets.lenderMetrics')}
      icon={<BriefcaseIcon className="h-5 w-5" />}
      accent="analytics"
      variant="gradient"
      className={className}
      loading={loading || !data}
    >
      <div className="flex flex-row items-center justify-center h-full p-3 gap-4">
        {/* Total Lenders */}
        <div className="text-center flex-1 flex flex-col justify-center">
          <div className="text-4xl font-bold text-white tabular-nums mb-1">
            {data?.totalLenders || 0}
          </div>
          <div className="text-xs text-white/90 uppercase tracking-wide font-medium whitespace-nowrap">
            {t('dashboard.widgets.totalLenders')}
          </div>
          <div className="text-xs text-white/70 mt-0.5 whitespace-nowrap">
            {t('dashboard.widgets.lastYear')}
          </div>
        </div>

        {/* Divider */}
        <div className="w-px h-16 bg-white/40" />

        {/* Majority Lender */}
        <div className="text-center flex-1 flex flex-col justify-center">
          <div className="text-xs text-white/80 uppercase tracking-wide mb-1.5 font-medium whitespace-nowrap">
            {t('dashboard.widgets.majorityLender')}
          </div>
          {data?.dominantLender ? (
            <div>
              <div className="text-base font-bold text-white truncate px-1 mb-0.5">
                {data.dominantLender.name}
              </div>
              <div className="text-sm text-white/90 font-semibold whitespace-nowrap">
                {data.dominantLender.percentage}% {t('dashboard.widgets.ofDeals')}
              </div>
            </div>
          ) : (
            <div className="text-lg font-semibold text-white/70">
              N/A
            </div>
          )}
        </div>
      </div>
    </BaseWidget>
  )
}

export default LenderMetricsWidget

